/**
 * NotificationDefaultsTab — Admin > Notifications (notifications part
 * two): the tenant-wide defaults every person who never expressed a
 * preference follows.
 *
 * Same grid shape as the personal preferences page
 * (NotificationSettings.tsx) — one row per catalogue event, a channel
 * control and a cadence control (disabled when the channel isn't
 * email) — editing tenants.config.notifications.defaults instead of a
 * person's own row. Gate: manage_team (the rateBands.ts precedent for
 * this tenants.config seam), mirrored here by the caller
 * (AdminReview.tsx's hideAiToolsTabs flag, the same gate rate-band-editor
 * shares).
 */

import { type ReactNode } from 'react';
import {
  useAdminNotificationDefaults,
  useSetAdminNotificationDefaults,
} from '@/lib/notifications';
import type { NotificationRule, TenantNotificationDefaultRow } from '@/lib/api';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { SelectField } from '@/components/primitives/SelectField';

const CHANNEL_OPTIONS: ReadonlyArray<{ value: NotificationRule['channel']; label: string }> = [
  { value: 'email_and_in_app', label: 'Email and in-app' },
  { value: 'in_app', label: 'In-app only' },
  { value: 'off', label: 'Off' },
];

const CADENCE_OPTIONS: ReadonlyArray<{ value: NotificationRule['cadence']; label: string }> = [
  { value: 'immediate', label: 'Immediately' },
  { value: 'daily', label: 'Daily digest at 7am' },
];

export interface NotificationDefaultsTabProps {
  tenantSlug: string;
}

export function NotificationDefaultsTab({ tenantSlug }: NotificationDefaultsTabProps): ReactNode {
  const { rows, isLoading, isError, error } = useAdminNotificationDefaults(tenantSlug);
  const setDefaults = useSetAdminNotificationDefaults(tenantSlug);

  function save(row: TenantNotificationDefaultRow, rule: NotificationRule): void {
    setDefaults.mutate({ [row.event_key]: rule });
  }

  return (
    <div>
      <p className="text-sm text-ink-soft mb-4">
        These are the defaults for anyone who has not set their own rule. A person's own choice on
        their notification settings page always wins over what you set here.
      </p>

      <AsyncBoundary what="the notification defaults" isLoading={isLoading} isError={isError} error={error} emptyHeadline="Once there are events to set, you'll see them here.">
        <ul className="border border-sage-800/[8%] rounded-card divide-y divide-sage-800/[8%]">
          {rows.map((row) => {
            const cadenceDisabled = row.channel !== 'email_and_in_app';
            return (
              <li
                key={row.event_key}
                className="p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 sm:max-w-sm">
                  <p className="text-sm font-medium text-ink">{row.label}</p>
                  <p className="mt-0.5 text-xs text-ink-soft">{row.description}</p>
                  {row.is_tenant_set ? (
                    <span
                      data-testid={`admin-set-marker-${row.event_key}`}
                      className="mt-1 inline-block text-xs font-medium text-warm-600"
                    >
                      Set for your organization
                    </span>
                  ) : (
                    <span
                      data-testid={`admin-default-marker-${row.event_key}`}
                      className="mt-1 inline-block text-xs font-medium text-sage-600"
                    >
                      Platform default
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-3 min-w-0 sm:flex-row sm:flex-wrap sm:items-center">
                  <SelectField size="default" className="w-full sm:w-auto sm:shrink-0"
                    aria-label={`Default channel for ${row.label}`}
                    data-testid={`admin-channel-${row.event_key}`}
                    value={row.channel}
                    onChange={(e) =>
                      save(row, {
                        channel: e.target.value as NotificationRule['channel'],
                        cadence: row.cadence,
                      })
                    }
                  >
                    {CHANNEL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectField>

                  <SelectField size="default" className="w-full sm:w-auto sm:shrink-0"
                    aria-label={`Default cadence for ${row.label}`}
                    data-testid={`admin-cadence-${row.event_key}`}
                    value={row.cadence}
                    disabled={cadenceDisabled}
                    onChange={(e) =>
                      save(row, { channel: row.channel, cadence: e.target.value as NotificationRule['cadence'] })
                    }
                  >
                    {CADENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </li>
            );
          })}
        </ul>
      </AsyncBoundary>
    </div>
  );
}
